import { cn } from "@/lib/utils";

/**
 * Discrete 1–N rating scale — a row of numbered buttons with one active. Reach
 * for this for planning-poker style scoring, effort/impact votes, confidence
 * ratings, any bounded whole-number pick where seeing the whole range matters
 * (unlike NumberInput, which hides the range behind a stepper).
 */
export interface ScaleInputProps {
  value: number | null;
  onChange: (value: number) => void;
  /** Inclusive bounds (default 1–10). */
  min?: number;
  max?: number;
  /** Optional fixed-width label to the left, so stacked scales line up. */
  label?: string;
  disabled?: boolean;
  className?: string;
}

export function ScaleInput({
  value,
  onChange,
  min = 1,
  max = 10,
  label,
  disabled = false,
  className,
}: ScaleInputProps) {
  const steps = Array.from({ length: max - min + 1 }, (_, i) => min + i);
  return (
    <div className={cn("flex items-center gap-2", className)}>
      {label ? <span className="w-12 shrink-0 text-xs font-medium">{label}</span> : null}
      <div className="flex flex-1 gap-1">
        {steps.map((n) => (
          <button
            key={n}
            type="button"
            disabled={disabled}
            aria-pressed={value === n}
            onClick={() => onChange(n)}
            className={cn(
              "h-7 flex-1 rounded border text-xs font-medium transition-colors disabled:opacity-50",
              value === n
                ? "border-brand bg-brand text-white"
                : "border-border bg-background hover:border-brand/50",
            )}
          >
            {n}
          </button>
        ))}
      </div>
    </div>
  );
}
